import { ref, readonly } from 'vue'

function isRunningInTelegram() {
  const webApp = window.Telegram?.WebApp
  if (!webApp) return false
  if (webApp.initData && webApp.initData.length > 0) return true
  if (window.TelegramWebviewProxy) return true
  return false
}

function forceRequestFullscreen() {
  try {
    if (window.TelegramWebviewProxy?.postEvent) {
      window.TelegramWebviewProxy.postEvent('web_app_request_fullscreen', '')
    }
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        JSON.stringify({ eventType: 'web_app_request_fullscreen' }),
        '*'
      )
    }
  } catch (err) {
    console.error('[TG] Error sending web_app_request_fullscreen:', err)
  }
}

function forceDisableVerticalSwipes() {
  try {
    if (window.TelegramWebviewProxy?.postEvent) {
      window.TelegramWebviewProxy.postEvent(
        'web_app_setup_swipe_behavior',
        JSON.stringify({ allow_vertical_swipe: false })
      )
    }
  } catch (err) {
    console.error('[TG] Error disabling vertical swipes:', err)
  }
}

function setupSafeAreaCssVars() {
  const webApp = window.Telegram?.WebApp
  if (!webApp) return false

  const safeAreaInset = webApp.safeAreaInset
  const contentSafeAreaInset = webApp.contentSafeAreaInset
  const root = document.documentElement

  const platform = (webApp.platform || 'unknown').toLowerCase()
  const isMobile = platform === 'android' || platform === 'ios'

  let hasValidValues = false

  if (safeAreaInset) {
    root.style.setProperty('--tg-safe-area-top', `${safeAreaInset.top ?? 0}px`)
    root.style.setProperty('--tg-safe-area-bottom', `${safeAreaInset.bottom ?? 0}px`)
    root.style.setProperty('--tg-safe-area-left', `${safeAreaInset.left ?? 0}px`)
    root.style.setProperty('--tg-safe-area-right', `${safeAreaInset.right ?? 0}px`)
    if ((safeAreaInset.top ?? 0) > 0 || (safeAreaInset.bottom ?? 0) > 0) {
      hasValidValues = true
    }
  }

  if (contentSafeAreaInset) {
    if ((contentSafeAreaInset.top ?? 0) > 0) {
      root.style.setProperty('--tg-content-safe-area-top', `${contentSafeAreaInset.top}px`)
      hasValidValues = true
    }
    if ((contentSafeAreaInset.bottom ?? 0) > 0) {
      root.style.setProperty('--tg-content-safe-area-bottom', `${contentSafeAreaInset.bottom}px`)
    }
  }

  // 仅在移动端全屏模式下设置默认的 56px 偏移
  if (isMobile && webApp.isFullscreen === true && (!contentSafeAreaInset || (contentSafeAreaInset.top ?? 0) === 0)) {
    root.style.setProperty('--tg-content-safe-area-top', '56px')
    hasValidValues = true
  }

  return hasValidValues
}

export function useTelegram() {
  const isReady = ref(false)
  const webApp = ref(null)
  const isFullscreen = ref(false)
  const inTelegram = ref(false)
  const logs = ref([])

  function log(msg) {
    const timestamp = new Date().toLocaleTimeString()
    const fullMsg = `[${timestamp}] ${msg}`
    console.log(fullMsg)
    logs.value.push(fullMsg)
    if (logs.value.length > 50) logs.value.shift()
  }

  function init() {
    log('Init started')
    const tgWebApp = window.Telegram?.WebApp ?? null
    webApp.value = tgWebApp

    const isTG = isRunningInTelegram()
    inTelegram.value = isTG
    log(`Inside Telegram: ${isTG}`)

    if (isTG && tgWebApp) {
      log('Calling ready() and expand()')
      try {
        tgWebApp.ready()
        tgWebApp.expand()

        // 仅在移动端（iOS/Android）尝试进入全屏模式
        if (tgWebApp.platform === 'android' || tgWebApp.platform === 'ios') {
          log('Mobile platform detected, requesting fullscreen')
          forceRequestFullscreen()
        } else {
          log('PC/Desktop platform detected, skipping fullscreen')
        }

        forceDisableVerticalSwipes()
        isFullscreen.value = !!tgWebApp.isFullscreen
      } catch (e) {
        log(`Error in SDK calls: ${e.message}`)
      }
    } else {
      log('Not in Telegram or SDK missing, skipping ready sequence')
      isReady.value = true
      return
    }

    let attempts = 0
    const MAX_ATTEMPTS = 5
    const POLL_INTERVAL = 500

    const poll = () => {
      attempts++
      log(`Polling safe area (attempt ${attempts})...`)

      let hasValues = false
      try {
        hasValues = setupSafeAreaCssVars()
      } catch (e) {
        log(`Error in setupSafeAreaCssVars: ${e.message}`)
      }

      if (hasValues) {
        log('Safe area values detected.')
        isReady.value = true
      } else if (attempts >= MAX_ATTEMPTS) {
        log('Polling timeout, using defaults and proceeding.')
        try {
          setupSafeAreaCssVars()
          const platform = (webApp.value?.platform || 'unknown').toLowerCase()
          const isMobile = platform === 'android' || platform === 'ios'
          if (isMobile) {
            document.documentElement.style.setProperty('--tg-content-safe-area-top', '56px')
          }
        } catch (e) { }
        isReady.value = true
      } else {
        setTimeout(poll, POLL_INTERVAL)
      }
    }

    setTimeout(() => {
      if (!isReady.value) {
        log('CRITICAL: Hard timeout reached, forcing app display.')
        isReady.value = true
      }
    }, 4000)

    setTimeout(poll, 100)
  }

  return {
    isReady: readonly(isReady),
    webApp: readonly(webApp),
    isFullscreen: readonly(isFullscreen),
    inTelegram: readonly(inTelegram),
    logs: readonly(logs),
    init,
    log,
  }
}
